<template>
  <section :class="$style.wrapper">
    <header :class="$style.header">
      <h2 :class="$style.title">メッセージリストの絞り込み</h2>
      <ButtonClose @click="$emit('close')" />
    </header>
    <section :class="$style.body">
      <section>
        <div :class="$style.inputWrapper">
          <div>
            <section :class="$style.inputSection">
              <h3 :class="$style.subtitle">対応状況</h3>
              <ul :class="$style.groupWrapper">
                <li>
                  <label :class="$style.checkbox">
                    <DolphinCheckbox v-model="selectedAllChatRoomStatuses" />
                    <span>全て選択</span>
                  </label>
                </li>
                <li>
                  <label
                    v-for="status in allChatRoomStatuses"
                    :key="status"
                    :class="$style.checkbox"
                  >
                    <DolphinCheckbox
                      v-model="selectedStatuses"
                      :value="status"
                    />
                    <span>{{ chatRoomStatusString[status] }}</span>
                  </label>
                </li>
              </ul>
            </section>
          </div>
          <div>
            <section :class="$style.inputSection">
              <h3 :class="$style.subtitle">チャット状況</h3>
              <ul :class="$style.groupWrapper">
                <li>
                  <label :class="$style.checkbox">
                    <DolphinCheckbox v-model="selectedAllChatMessageStatuses" />
                    <span>全て選択</span>
                  </label>
                </li>
                <li>
                  <label
                    v-for="status in allChatMessageStatuses"
                    :key="status"
                    :class="$style.checkbox"
                  >
                    <DolphinCheckbox
                      v-model="selectedChatMessageStatuses"
                      :value="status"
                    />
                    <span>{{ chatMessageStatusString[status] }}</span>
                  </label>
                </li>
              </ul>
            </section>
            <section :class="$style.inputSection">
              <h3 :class="$style.subtitle">メモ</h3>
              <ul :class="$style.groupWrapper">
                <li>
                  <label :class="$style.checkbox">
                    <DolphinCheckbox
                      v-model="selectedAllChatRoomMemoStatuses"
                    />
                    <span>全て選択</span>
                  </label>
                </li>
                <li>
                  <label
                    v-for="status in allChatRoomMemoStatuses"
                    :key="status"
                    :class="$style.checkbox"
                  >
                    <DolphinCheckbox
                      v-model="selectedChatRoomMemoStatuses"
                      :value="status"
                    />
                    <span>{{ chatRoomMemoStatusString[status] }}</span>
                  </label>
                </li>
              </ul>
            </section>
          </div>
        </div>
        <div :class="$style.inputWrapper">
          <section>
            <h3 :class="$style.subtitle">最新メッセージの日時</h3>
            <div :class="$style.lastMessageInputGroup">
              <DolphinDateInput
                v-model="lastMessageSentAt.startedAt"
                clearable
              />
              <p>～</p>
              <DolphinDateInput v-model="lastMessageSentAt.endedAt" clearable />
            </div>
          </section>
        </div>
      </section>
      <footer :class="[$style.body__buttonWrapper, $style.buttonWrapper]">
        <FlatButton
          :disabled="isButtonDisabled"
          variant="primary"
          size="medium"
          variation="primary"
          @click="submit"
        >
          この内容で絞り込む
        </FlatButton>
      </footer>
    </section>
  </section>
</template>

<script lang="ts" setup>
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import DolphinDateInput from '@/components/atoms/DolphinDateInput.vue'
import { computed, ref } from 'vue'
import type {
  ChatRoomStatus,
  ChatMessageStatus,
  ChatRoomFilterInput,
  ChatRoomMemoStatus,
} from '@/types/schema.generated'
import {
  allChatRoomStatuses,
  chatRoomStatusString,
} from '@/models/chatRoom/chatRoomStatus'
import {
  allChatRoomMemoStatuses,
  chatRoomMemoStatusString,
} from '@/models/chatRoom/chatRoomMemoStatus'

import {
  allChatMessageStatuses,
  chatMessageStatusString,
} from '@/models/chatRoom/chatMessageStatus'
import { parseTimeString } from '@/libs/utilities/gql-scalar'
import FlatButton from '@/components/atoms/FlatButton.vue'

const props = defineProps<{
  modelValue: ChatRoomFilterInput
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'update:modelValue', value: ChatRoomFilterInput): void
}>()

const selectedStatuses = ref<ChatRoomStatus[]>(
  props.modelValue.statuses ?? [...allChatRoomStatuses]
)
const selectedChatMessageStatuses = ref<ChatMessageStatus[]>(
  props.modelValue.chatMessageStatuses ?? [...allChatMessageStatuses]
)
const selectedChatRoomMemoStatuses = ref<ChatRoomMemoStatus[]>(
  props.modelValue.memoStatuses ?? [...allChatRoomMemoStatuses]
)
const lastMessageSentAt = ref<{ startedAt: Date | null; endedAt: Date | null }>(
  {
    startedAt: props.modelValue.lastMessageSentAt?.startedAt
      ? new Date(props.modelValue.lastMessageSentAt.startedAt)
      : null,
    endedAt: props.modelValue.lastMessageSentAt?.endedAt
      ? new Date(props.modelValue.lastMessageSentAt.endedAt)
      : null,
  }
)

const selectedAllChatRoomStatuses = computed({
  get: () => selectedStatuses.value.length === allChatRoomStatuses.length,
  set: () => {
    selectedStatuses.value = selectedAllChatRoomStatuses.value
      ? []
      : [...allChatRoomStatuses]
  },
})

const selectedAllChatMessageStatuses = computed({
  get: () =>
    selectedChatMessageStatuses.value.length === allChatMessageStatuses.length,
  set: () => {
    selectedChatMessageStatuses.value = selectedAllChatMessageStatuses.value
      ? []
      : [...allChatMessageStatuses]
  },
})

const selectedAllChatRoomMemoStatuses = computed({
  get: () =>
    selectedChatRoomMemoStatuses.value.length ===
    allChatRoomMemoStatuses.length,
  set: () => {
    selectedChatRoomMemoStatuses.value = selectedAllChatRoomMemoStatuses.value
      ? []
      : [...allChatRoomMemoStatuses]
  },
})

const submit = () => {
  // 選択肢の上から順になるように渡す
  const chatRoomStatuses = (() => {
    // すべて選択されているなら何も指定しなくてもよい
    if (selectedAllChatRoomStatuses.value) {
      return null
    }
    return allChatRoomStatuses.filter(status =>
      selectedStatuses.value.includes(status)
    )
  })()

  const chatMessageStatuses = (() => {
    // すべて選択されているなら何も指定しなくてもよい
    //
    // 最新メッセージが Zoom 連携メッセージなどシステム系のメッセージになると、未読・未返信・返信済に該当しないので、表示されなくなる
    // そのため、すべて選択されている場合は、チャット状況の指定をしない
    if (selectedAllChatMessageStatuses.value) {
      return null
    }

    return allChatMessageStatuses.filter(status =>
      selectedChatMessageStatuses.value.includes(status)
    )
  })()

  const sentAt = lastMessageSentAt.value

  emit('update:modelValue', {
    statuses: chatRoomStatuses,
    chatMessageStatuses,
    memoStatuses: selectedChatRoomMemoStatuses.value,
    lastMessageSentAt: {
      startedAt: sentAt.startedAt ? parseTimeString(sentAt.startedAt) : null,
      endedAt: sentAt.endedAt ? parseTimeString(sentAt.endedAt) : null,
    },
  })
}

const isButtonDisabled = computed(() => {
  return (
    selectedStatuses.value.length === 0 ||
    selectedChatMessageStatuses.value.length === 0 ||
    selectedChatRoomMemoStatuses.value.length === 0
  )
})
</script>

<style lang="scss" module>
.wrapper {
  width: 90vw;
  max-width: 620px;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.title {
  font-size: 16px;
  font-weight: bold;
}

.subtitle {
  font-weight: bold;
}

.body {
  padding: 24px 40px 40px;

  &__buttonWrapper {
    margin-top: 44px;
  }
}

.groupWrapper {
  display: flex;
  margin-top: 12px;

  > *:nth-child(n + 2) {
    margin-left: 44px;
  }
}

.inputWrapper {
  display: flex;
  justify-content: space-between;

  &:nth-child(n + 2) {
    margin-top: 36px;
  }
}

.inputSection {
  &:nth-child(n + 2) {
    margin-top: 36px;
  }
}

.checkbox {
  display: flex;
  align-items: center;

  &:nth-child(n + 2) {
    margin-top: 18px;
  }

  > *:nth-child(n + 2) {
    margin-left: 4px;
  }
}

.lastMessageInputGroup {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.buttonWrapper {
  display: flex;
  justify-content: center;
}
</style>
